import { useCallback, useEffect, useState } from 'react';
import { X, UploadCloud, Loader2, Check, AlertTriangle, RefreshCw, Undo2, Package, Server } from 'lucide-react';
import { apiService, PackGroupDTO, PackContainer, PackConflictDTO, ContainerConflictDTO } from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import { useAuthStore } from '@/stores/auth.store';
import { projectMatches } from '@/config/games';
import { clearConfigListCache } from './configListCache';
import { ConflictList, SyncConflictItem } from './SyncConflicts';
import { followRelease } from './followRelease';
import styles from './DeployPanel.module.scss';

interface GroupChanges {
  group: PackGroupDTO;
  files: string[];
  /** Fichiers modifiés ici ET sur GitHub : version du panel gardée, publication bloquée. */
  conflicts: PackConflictDTO[];
}

interface ContainerChanges {
  container: PackContainer;
  files: string[];
  /** Fichiers modifiés ici ET sur main : version du panel gardée, publication bloquée. */
  conflicts: ContainerConflictDTO[];
}

/** Ligne du compte rendu d'un envoi : une par groupe de packs ou image ; `ok` null = en cours. */
interface SendLine {
  key: string;
  label: string;
  ok: boolean | null;
  msg: string;
}

/**
 * Revue avant envoi, en 2 onglets aux procédures DISTINCTES :
 * - Ressource (behavior + resource pack) : sélection de fichiers → develop → PR → main → release.
 * - Config (containers data/mods) : sélection de containers → v<next> → PR CI → merge → release + Docker.
 * Qui a le droit de valider les demandes (« Publications » en écriture) PUBLIE tout de suite : plus de
 * passage par la page des demandes, une ligne au journal suffit. Les autres envoient une demande, qu'un
 * ayant droit accepte ou refuse. Une image publiée ne change que ses réglages : les versions des mods
 * montées par la CI sont remises avant le merge (côté serveur).
 * À la publication, le travail du panel est rejoué par-dessus la dernière version de GitHub (packs :
 * fusion par clé des atlas/blocks.json/.lang ; containers : fusion des JSON clé par clé sur main) ;
 * un fichier changé des deux côtés est un conflit à trancher ici.
 */
export function DeployPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const activeGameId = useGameStore((s) => s.activeGameId);
  // Même droit que la validation des demandes : le serveur le revérifie et, sans lui, crée une demande.
  const canPublish = useAuthStore((s) => s.canWrite)('config.requests');
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState<'ressource' | 'config'>('ressource');
  const [groups, setGroups] = useState<GroupChanges[]>([]);
  const [containers, setContainers] = useState<ContainerChanges[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectedContainers, setSelectedContainers] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [confirmRevert, setConfirmRevert] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [lines, setLines] = useState<SendLine[]>([]);
  const [resolving, setResolving] = useState<string | null>(null);

  const fileKey = (gid: string, f: string) => `${gid}::${f}`;

  const refresh = useCallback(async () => {
    setLoading(true);
    setConfirmRevert(false);
    setFeedback(null);
    // Ressource (packs) du projet actif
    const gr = await apiService.getPacksGroups();
    const list = (gr.data?.groups ?? []).filter((g) => projectMatches(g.projects, activeGameId));
    const packResults = await Promise.all(
      list.map(async (g) => {
        const [mf, cf] = await Promise.all([apiService.getPackModifiedFiles(g.id), apiService.getPackConflicts(g.id)]);
        return { group: g, files: mf.data?.files ?? [], conflicts: cf.data?.conflicts ?? [] };
      }),
    );
    const packChanges = packResults.filter((r) => r.files.length > 0);
    setGroups(packChanges);
    const all = new Set<string>();
    packChanges.forEach((r) => r.files.forEach((f) => all.add(fileKey(r.group.id, f))));
    setSelected(all);
    // Config (containers) du projet actif
    const cr = await apiService.getContainers();
    const clist = (cr.data?.containers ?? []).filter((c) => (c.project ?? 'paladium') === activeGameId);
    const contResults = await Promise.all(
      clist.map(async (c) => {
        const [mf, cf] = await Promise.all([apiService.getContainerModifiedFiles(c.id), apiService.getContainerConflicts(c.id)]);
        return { container: c, files: mf.data?.files ?? [], conflicts: cf.data?.conflicts ?? [] };
      }),
    );
    const contChanges = contResults.filter((r) => r.files.length > 0);
    setContainers(contChanges);
    setSelectedContainers(new Set(contChanges.map((r) => r.container.id)));
    setTab(packChanges.length > 0 || contChanges.length === 0 ? 'ressource' : 'config');
    setLoading(false);
  }, [activeGameId]);

  useEffect(() => {
    if (open) { setLines([]); refresh(); }
  }, [open, refresh]);

  if (!open) return null;

  const toggle = (k: string) =>
    setSelected((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  const toggleGroup = (gid: string, files: string[]) =>
    setSelected((s) => {
      const n = new Set(s);
      const allSel = files.every((f) => n.has(fileKey(gid, f)));
      files.forEach((f) => (allSel ? n.delete(fileKey(gid, f)) : n.add(fileKey(gid, f))));
      return n;
    });

  const toggleContainer = (id: string) =>
    setSelectedContainers((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const allPackKeys = groups.flatMap((gc) => gc.files.map((f) => fileKey(gc.group.id, f)));
  const allContainerIds = containers.map((c) => c.container.id);
  const packAllSelected = allPackKeys.length > 0 && allPackKeys.every((k) => selected.has(k));
  const containerAllSelected = allContainerIds.length > 0 && allContainerIds.every((id) => selectedContainers.has(id));
  const selectAllPacks = (on: boolean) => setSelected(on ? new Set(allPackKeys) : new Set());
  const selectAllContainers = (on: boolean) => setSelectedContainers(on ? new Set(allContainerIds) : new Set());

  const packSelected = selected.size;
  const totalSelected = packSelected + selectedContainers.size;
  const label = (f: string) => f.replace(/^(behavior|resource)\//, '');
  const nothing = groups.length === 0 && containers.length === 0;

  const renderSection = (gid: string, title: string, arr: string[]) =>
    arr.length === 0 ? null : (
      <div className={styles.section}>
        <div className={styles.sectionTitle}>{title}</div>
        {arr.map((f) => {
          const k = fileKey(gid, f);
          return (
            <label key={k} className={styles.fileRow}>
              <input type="checkbox" checked={selected.has(k)} onChange={() => toggle(k)} />
              <span className={styles.filePath}>{label(f)}</span>
            </label>
          );
        })}
      </div>
    );

  // Conflit : garder la version du panel (elle écrasera celle de GitHub) ou prendre celle de GitHub
  // (annulation du fichier ; la version du panel reste dans la sauvegarde sur le serveur).
  const settleConflict = async (gc: GroupChanges, c: SyncConflictItem | PackConflictDTO, keep: 'panel' | 'github') => {
    setResolving(`${gc.group.id}::${c.path}`);
    setFeedback(null);
    const res = keep === 'panel'
      ? await apiService.resolvePackConflicts(gc.group.id, [c.path])
      : await apiService.revertPackFiles(gc.group.id, [c.path]);
    clearConfigListCache(gc.group.id);
    setResolving(null);
    const ok = !!res.data?.success;
    setFeedback({
      ok,
      msg: ok
        ? keep === 'panel'
          ? `${label(c.path)} : version du panel gardée.`
          : `${label(c.path)} : version GitHub prise (celle du panel reste dans ${c.backup || 'la sauvegarde'}).`
        : res.data?.message ?? res.error ?? 'Échec',
    });
    await refresh();
  };

  // Conflit de container : garder la version du panel, ou prendre celle de main (pour un JSON fusionné,
  // seules les clés en conflit reprennent la valeur de main, le reste du travail du panel est gardé).
  const settleContainerConflict = async (cc: ContainerChanges, c: SyncConflictItem, keep: 'panel' | 'github') => {
    setResolving(`${cc.container.id}::${c.path}`);
    setFeedback(null);
    const res = keep === 'panel'
      ? await apiService.resolveContainerConflicts(cc.container.id, [c.path])
      : await apiService.takeMainContainerConflicts(cc.container.id, [c.path]);
    setResolving(null);
    const ok = !!res.data?.success;
    setFeedback({
      ok,
      msg: ok
        ? keep === 'panel'
          ? `${c.path} : version du panel gardée.`
          : `${c.path} : version de main prise (celle du panel reste dans ${c.backup || 'la sauvegarde'}).`
        : res.data?.message ?? res.error ?? 'Échec',
    });
    await refresh();
  };

  /** Chemin en cours de résolution dans le groupe ou le container `id`, sinon null. */
  const busyIn = (id: string) => (resolving?.startsWith(`${id}::`) ? resolving.slice(id.length + 2) : null);

  // Annule (git restore) les éditions sélectionnées : packs (behavior + resource) ET config (containers).
  const revertAll = async () => {
    if (!confirmRevert) { setConfirmRevert(true); return; }
    setConfirmRevert(false);
    setReverting(true);
    setFeedback(null);
    setLogs([]);
    const msgs: string[] = [];
    let ok = true;
    for (const gc of groups) {
      const files = gc.files.filter((f) => selected.has(fileKey(gc.group.id, f)));
      if (files.length === 0) continue;
      const res = await apiService.revertPackFiles(gc.group.id, files);
      clearConfigListCache(gc.group.id);
      if (res.data) { ok = ok && res.data.success; msgs.push(`[${gc.group.name || gc.group.id}] ${res.data.message}`); }
      else { ok = false; msgs.push(`[${gc.group.name || gc.group.id}] ${res.error ?? 'Échec'}`); }
    }
    for (const cc of containers) {
      if (!selectedContainers.has(cc.container.id)) continue;
      const res = await apiService.revertContainerFiles(cc.container.id, cc.files);
      if (res.data) { ok = ok && res.data.success; msgs.push(`[${cc.container.name}] ${res.data.message}`); }
      else { ok = false; msgs.push(`[${cc.container.name}] ${res.error ?? 'Échec'}`); }
    }
    setReverting(false);
    setLogs(msgs);
    setFeedback({ ok, msg: ok ? 'Modifications annulées (packs + config, restaurées depuis git).' : 'Des erreurs sont survenues (voir détails).' });
    await refresh();
  };

  // Un envoi par groupe de pack (fichiers sélectionnés) et par container sélectionné, en parallèle (dépôts
  // distincts). Le serveur publie tout de suite pour un ayant droit, sinon il crée une demande.
  const send = async () => {
    setConfirmRevert(false);
    setPublishing(true);
    setFeedback(null);
    setLogs([]);
    type Target = { key: string; label: string; input: Parameters<typeof apiService.sendDeploy>[0] };
    const targets: Target[] = [];
    for (const gc of groups) {
      const files = gc.files.filter((f) => selected.has(fileKey(gc.group.id, f)));
      if (files.length === 0) continue;
      const name = gc.group.name || gc.group.id;
      targets.push({ key: `pack::${gc.group.id}`, label: `Pack ${name}`, input: {
        project: activeGameId, type: 'pack', targetId: gc.group.id, targetName: name, files, message: message || undefined,
      } });
    }
    for (const cc of containers) {
      if (!selectedContainers.has(cc.container.id)) continue;
      targets.push({ key: `container::${cc.container.id}`, label: `Image ${cc.container.name}`, input: {
        project: activeGameId, type: 'container', targetId: cc.container.id, targetName: cc.container.name,
        files: cc.files, message: message || undefined,
      } });
    }
    const set = (key: string, ok: boolean | null, msg: string) =>
      setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ok, msg } : l)));
    setLines(targets.map((t) => ({ key: t.key, label: t.label, ok: null, msg: canPublish ? 'Publication…' : 'Envoi de la demande…' })));

    const outcomes = await Promise.all(targets.map(async (t): Promise<'published' | 'requested' | 'failed'> => {
      const res = await apiService.sendDeploy(t.input);
      const d = res.data;
      if (!d) { set(t.key, false, res.error ?? 'Échec'); return 'failed'; }
      if (!d.direct) { set(t.key, true, d.message); return 'requested'; }
      if (!d.published || !d.result) {
        const conflicts = d.result?.conflicts ?? [];
        set(t.key, false, (d.result?.message ?? d.message) + (conflicts.length ? ` (conflit : ${conflicts.join(', ')})` : ''));
        return 'failed';
      }
      if (t.input.type === 'pack') clearConfigListCache(t.input.targetId);
      const r = d.result;
      if (r.needsFinalize && r.containerId && r.version) {
        set(t.key, null, `${r.version} poussée, l'image se prépare…`);
        const f = await followRelease(r.containerId, r.version, (m) => set(t.key, null, m));
        set(t.key, f.ok, f.ok ? `${f.msg} À mettre en service dans Déploiement.` : f.msg);
        return f.ok ? 'published' : 'failed';
      }
      set(t.key, true, r.message || 'Publié.');
      return 'published';
    }));

    setPublishing(false);
    const n = (k: string) => outcomes.filter((o) => o === k).length;
    const failed = n('failed');
    const parts = [
      n('published') > 0 ? `${n('published')} publié(s)` : '',
      n('requested') > 0 ? `${n('requested')} demande(s) envoyée(s), à valider par une personne qui a le droit de publier` : '',
      failed > 0 ? `${failed} en échec (voir le détail)` : '',
    ].filter(Boolean);
    await refresh();
    setFeedback({ ok: failed === 0, msg: parts.join(' · ') || 'Rien à envoyer.' });
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.head}>
          <div className={styles.title}>
            <UploadCloud size={18} /> Envoyer
          </div>
          <div className={styles.headActions}>
            <button className={styles.iconBtn} onClick={refresh} title="Rafraîchir" disabled={loading || publishing}>
              <RefreshCw size={16} className={loading ? styles.spin : undefined} />
            </button>
            <button className={styles.iconBtn} onClick={onClose} title="Fermer">
              <X size={18} />
            </button>
          </div>
        </header>

        <div className={styles.body}>
          {loading ? (
            <div className={styles.center}>
              <Loader2 size={22} className={styles.spin} /> Analyse des modifications…
            </div>
          ) : nothing ? (
            <div className={styles.center}>Aucune modification à envoyer.</div>
          ) : (
            <>
              <div className={styles.tabBar}>
                <button className={`${styles.tab} ${tab === 'ressource' ? styles.tabActive : ''}`} onClick={() => setTab('ressource')}>
                  <Package size={14} /> Ressource ({groups.length})
                </button>
                <button className={`${styles.tab} ${tab === 'config' ? styles.tabActive : ''}`} onClick={() => setTab('config')}>
                  <Server size={14} /> Config ({containers.length})
                </button>
              </div>

              {tab === 'ressource' && (
                groups.length === 0 ? (
                  <div className={styles.center}>Aucun pack modifié.</div>
                ) : (
                  <>
                    <label className={styles.selAllRow}>
                      <input type="checkbox" checked={packAllSelected} onChange={(e) => selectAllPacks(e.target.checked)} />
                      Tout sélectionner ({allPackKeys.length} fichier{allPackKeys.length > 1 ? 's' : ''})
                    </label>
                    {groups.map((gc) => {
                      const allSel = gc.files.every((f) => selected.has(fileKey(gc.group.id, f)));
                      return (
                        <div key={gc.group.id} className={styles.group}>
                          <label className={styles.groupHead}>
                            <input type="checkbox" checked={allSel} onChange={() => toggleGroup(gc.group.id, gc.files)} />
                            <span className={styles.groupBadge} style={{ borderColor: gc.group.color || '#8B3FF1', color: gc.group.color || '#8B3FF1' }}>
                              {gc.group.name || gc.group.id}
                            </span>
                            <span className={styles.groupCount}>{gc.files.length} fichier{gc.files.length > 1 ? 's' : ''}</span>
                          </label>
                          <ConflictList
                            conflicts={gc.conflicts}
                            remoteLabel="GitHub"
                            busyPath={busyIn(gc.group.id)}
                            disabled={!!resolving}
                            onKeepPanel={(c) => void settleConflict(gc, c, 'panel')}
                            onTakeRemote={(c) => void settleConflict(gc, c, 'github')}
                          />
                          {renderSection(gc.group.id, 'Behavior pack', gc.files.filter((f) => f.startsWith('behavior/')))}
                          {renderSection(gc.group.id, 'Resource pack', gc.files.filter((f) => f.startsWith('resource/')))}
                        </div>
                      );
                    })}
                  </>
                )
              )}

              {tab === 'config' && (
                containers.length === 0 ? (
                  <div className={styles.center}>Aucun container modifié.</div>
                ) : (
                  <>
                    <label className={styles.selAllRow}>
                      <input type="checkbox" checked={containerAllSelected} onChange={(e) => selectAllContainers(e.target.checked)} />
                      Tout sélectionner ({containers.length} container{containers.length > 1 ? 's' : ''})
                    </label>
                    {containers.map((cc) => (
                      <div key={cc.container.id} className={styles.group}>
                        <label className={styles.groupHead}>
                          <input type="checkbox" checked={selectedContainers.has(cc.container.id)} onChange={() => toggleContainer(cc.container.id)} />
                          <span className={styles.groupBadge} style={{ borderColor: '#F97316', color: '#F97316' }}>{cc.container.name}</span>
                          <span className={styles.groupCount}>{cc.files.length} fichier{cc.files.length > 1 ? 's' : ''}</span>
                        </label>
                        <ConflictList
                          conflicts={cc.conflicts}
                          remoteLabel="main"
                          busyPath={busyIn(cc.container.id)}
                          disabled={!!resolving}
                          onKeepPanel={(c) => void settleContainerConflict(cc, c, 'panel')}
                          onTakeRemote={(c) => void settleContainerConflict(cc, c, 'github')}
                          takeTitle={(c) => ((c.keys ?? []).length > 0
                            ? 'Seules les clés en conflit reprennent la valeur de main, le reste du travail du panel est gardé.'
                            : 'Le fichier revient à la version de main.')}
                        />
                        <div className={styles.section}>
                          {cc.files.map((f) => (
                            <div key={f} className={styles.fileRow}><span className={styles.filePath}>{f}</span></div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </>
                )
              )}
            </>
          )}
        </div>

        {lines.length > 0 && (
          <div className={styles.sendLines}>
            {lines.map((l) => (
              <div key={l.key} className={`${styles.sendLine} ${l.ok === false ? styles.sendErr : l.ok ? styles.sendOk : ''}`}>
                <span className={styles.sendIcon}>
                  {l.ok === null ? <Loader2 size={12} className={styles.spin} /> : l.ok ? <Check size={12} /> : <AlertTriangle size={12} />}
                </span>
                <span><strong>{l.label}</strong> : {l.msg}</span>
              </div>
            ))}
          </div>
        )}

        {logs.length > 0 && (
          <div className={styles.logs}>
            {logs.map((l, i) => {
              const m = l.match(/https?:\/\/\S+/);
              return m ? (
                <a key={i} href={m[0]} target="_blank" rel="noreferrer" className={styles.logLink}>{l}</a>
              ) : (
                <div key={i} className={styles.logLine}>{l}</div>
              );
            })}
          </div>
        )}

        <footer className={styles.foot}>
          <input
            className={styles.msgInput}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Message (optionnel)"
          />
          {feedback && (
            <span className={feedback.ok ? styles.ok : styles.err}>
              {feedback.ok ? <Check size={14} /> : <AlertTriangle size={14} />} {feedback.msg}
            </span>
          )}
          <button
            className={`${styles.revertBtn} ${confirmRevert ? styles.revertBtnArmed : ''}`}
            onClick={revertAll}
            disabled={reverting || publishing || totalSelected === 0}
            title="Restaure depuis git les éléments sélectionnés (packs + config) — annule les éditions non publiées"
          >
            {reverting ? <Loader2 size={15} className={styles.spin} /> : <Undo2 size={15} />}
            {reverting ? 'Annulation…' : confirmRevert ? `Confirmer l'annulation (${totalSelected}) ?` : 'Annuler la sélection'}
          </button>
          <button
            className={styles.publishBtn}
            onClick={send}
            disabled={publishing || reverting || totalSelected === 0}
            title={canPublish
              ? "Publie tout de suite. Une image est refabriquée avec ses réglages seulement (versions des mods inchangées), puis se met en service dans Déploiement."
              : "Envoie une demande : une personne qui a le droit de publier la validera."}
          >
            {publishing ? <Loader2 size={15} className={styles.spin} /> : <UploadCloud size={15} />}
            {publishing
              ? (canPublish ? 'Publication…' : 'Envoi…')
              : canPublish ? `Publier (${totalSelected})` : `Demander la publication (${totalSelected})`}
          </button>
        </footer>
      </div>
    </div>
  );
}
